import { useEffect, useRef, useState } from 'react';
import type { AppConfig } from '../config.js';
import { AuthError, completeSignIn, startSignIn, type ChallengeHandle } from '../auth/cognito.js';
import { storeTokens } from '../auth/session.js';

const RESEND_COOLDOWN_SECONDS = 30;
const PENDING_KEY = 'rx.pendingChallenge';

/**
 * The shortest code worth submitting. Deliberately NOT the exact expected length:
 * Cognito's managed EMAIL_OTP decides how many digits it sends (it is currently 8,
 * not the 6 this screen originally assumed), and that is not ours to configure.
 * Hard-coding a length silently truncated pasted codes and produced a mismatch that
 * looked like the code itself was wrong.
 */
const MIN_CODE_DIGITS = 4;
const MAX_CODE_DIGITS = 12;

/**
 * Sign-in, designed around the constraint that there is no magic link.
 *
 * Cognito binds the auth session to the browser context that requested it, so a link
 * tapped in the Mail app opens a different context and can never complete. It has to
 * be a typed code, which means the person must leave for their email and come back —
 * the single most likely place for this to fail.
 *
 * So: the pending challenge is persisted in sessionStorage and restored on return,
 * the input is `inputmode="numeric"` with `autocomplete="one-time-code"` so iOS offers
 * the code straight above the keyboard, and resending is possible but rate-limited
 * because Cognito throttles repeats.
 */
export function SignIn({ config, onSignedIn }: { config: AppConfig; onSignedIn: () => void }) {
  const [email, setEmail] = useState('');
  const [challenge, setChallenge] = useState<ChallengeHandle | undefined>(() => restorePending());
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | undefined>();
  const [cooldown, setCooldown] = useState(0);
  /** True once the current challenge is dead and only a new code can help. */
  const [spent, setSpent] = useState(false);
  const codeRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (cooldown <= 0) return;
    const timer = window.setTimeout(() => {
      setCooldown((c) => c - 1);
    }, 1000);
    return () => {
      window.clearTimeout(timer);
    };
  }, [cooldown]);

  useEffect(() => {
    if (challenge) codeRef.current?.focus();
  }, [challenge]);

  async function requestCode(address: string): Promise<void> {
    setBusy(true);
    setError(undefined);
    try {
      const handle = await startSignIn(config, address.trim().toLowerCase());
      setChallenge(handle);
      setSpent(false);
      setCode('');
      // Survives the trip to the Mail app and back.
      sessionStorage.setItem(PENDING_KEY, JSON.stringify(handle));
      setCooldown(RESEND_COOLDOWN_SECONDS);
    } catch (e) {
      setError(messageFor(e));
    } finally {
      setBusy(false);
    }
  }

  async function submitCode(): Promise<void> {
    if (!challenge || spent) return;
    setBusy(true);
    setError(undefined);
    try {
      const tokens = await completeSignIn(config, challenge, code.trim());
      await storeTokens(tokens);
      sessionStorage.removeItem(PENDING_KEY);
      onSignedIn();
    } catch (e) {
      setError(messageFor(e));
      setCode('');
      // A Cognito sign-in session is SINGLE USE. One wrong code kills it, and a second
      // attempt against the dead session does not merely fail — it returns "Too many
      // invalid credentials attempts. User temporarily locked." So the only correct
      // recovery is a brand new code, and the UI must stop offering a retry that would
      // lock the account instead.
      if (isSessionSpent(e)) {
        setSpent(true);
        sessionStorage.removeItem(PENDING_KEY);
      }
    } finally {
      setBusy(false);
    }
  }

  if (!challenge) {
    return (
      <main className="app">
        <h1>Med Log</h1>
        <p className="muted">Sign in and we will email you a code.</p>
        <form
          className="stack"
          onSubmit={(e) => {
            e.preventDefault();
            void requestCode(email);
          }}
        >
          <input
            className="field"
            type="email"
            inputMode="email"
            autoComplete="email"
            autoCapitalize="off"
            autoCorrect="off"
            placeholder="your email"
            aria-label="Email address"
            value={email}
            onChange={(e) => {
              setEmail(e.target.value);
            }}
          />
          <button className="btn-primary" type="submit" disabled={busy || email.trim().length < 3}>
            {busy ? 'Sending…' : 'Email me a code'}
          </button>
        </form>
        {error !== undefined && <p className="error">{error}</p>}
      </main>
    );
  }

  return (
    <main className="app">
      <h1>Check your email</h1>
      <p className="muted">
        We sent a code to {challenge.destination ?? challenge.username}. It expires in 15
        minutes. If you asked for more than one, use the newest — a new code replaces the
        one before it.
      </p>
      <form
        className="stack"
        onSubmit={(e) => {
          e.preventDefault();
          void submitCode();
        }}
      >
        <input
          ref={codeRef}
          className="field code-field"
          type="text"
          inputMode="numeric"
          // iOS offers the emailed code directly above the keyboard, which removes
          // the need to memorise it on the trip back from Mail.
          autoComplete="one-time-code"
          pattern="[0-9]*"
          // No maxLength: the browser enforces it BEFORE onChange, so a paste longer
          // than the cap is truncated to a wrong code with nothing to show for it.
          // Digits are extracted here instead, which also tolerates pasting the code
          // with surrounding text from the email.
          maxLength={MAX_CODE_DIGITS}
          placeholder="code from the email"
          aria-label="Code from the email"
          value={code}
          disabled={spent}
          onChange={(e) => {
            setCode(e.target.value.replace(/\D/g, '').slice(0, MAX_CODE_DIGITS));
          }}
        />
        <button
          className="btn-primary"
          type="submit"
          disabled={busy || spent || code.length < MIN_CODE_DIGITS}
        >
          {busy ? 'Checking…' : 'Sign in'}
        </button>
      </form>
      {error !== undefined && <p className="error">{error}</p>}
      <div className="btn-row">
        <button
          className={spent ? 'btn-primary' : 'btn-quiet'}
          type="button"
          // After a spent challenge the cooldown is irrelevant: a new code is the only
          // way forward, so never make someone wait to escape a dead end.
          disabled={busy || (!spent && cooldown > 0)}
          onClick={() => {
            void requestCode(challenge.username);
          }}
        >
          {spent
            ? 'Email me a new code'
            : cooldown > 0
              ? `Resend in ${String(cooldown)}s`
              : 'Send a new code'}
        </button>
        <button
          className="btn-quiet"
          type="button"
          onClick={() => {
            sessionStorage.removeItem(PENDING_KEY);
            setChallenge(undefined);
            setCode('');
            setError(undefined);
          }}
        >
          Use a different email
        </button>
      </div>
    </main>
  );
}

function restorePending(): ChallengeHandle | undefined {
  try {
    const raw = sessionStorage.getItem(PENDING_KEY);
    return raw ? (JSON.parse(raw) as ChallengeHandle) : undefined;
  } catch {
    return undefined;
  }
}

/**
 * A Cognito sign-in session is single use.
 *
 * Verified against the real service: one wrong code returns CodeMismatchException and
 * kills the session; a SECOND attempt on that same session returns "Too many invalid
 * credentials attempts. User temporarily locked." So a failed attempt must never be
 * followed by another attempt — only by a fresh code.
 */
function isSessionSpent(error: unknown): boolean {
  if (!(error instanceof AuthError)) return false;
  return [
    'CodeMismatchException',
    'ExpiredCodeException',
    'NotAuthorizedException',
    'NotAuthorized',
  ].includes(error.code);
}

function messageFor(error: unknown): string {
  if (error instanceof AuthError) {
    switch (error.code) {
      case 'CodeMismatchException':
        // Cognito's own wording here is "Invalid code or auth state for the user",
        // which tells an ordinary person nothing and reads like a system fault.
        return 'That code did not match. Codes can only be used once — tap below and we will send a new one.';
      case 'ExpiredCodeException':
        return 'That code has expired. Tap below for a new one.';
      case 'NotAuthorizedException':
      case 'NotAuthorized':
        return 'That code did not work, and this sign-in has now timed out. Tap below for a new code — you may need to wait a few seconds.';
      case 'UserNotFoundException':
        // Deliberately vague: the pool is invite-only, and confirming which addresses
        // exist would leak who is in the system.
        return 'That did not work. Check the email address and try again.';
      case 'TooManyRequestsException':
      case 'LimitExceededException':
        return 'Too many attempts just now. Please wait a minute and try again.';
      default:
        return error.message;
    }
  }
  return 'Something went wrong. Please try again.';
}
